import React from 'react';
import LmsRolePage from './LmsRolePage';

const relatedLinks = [
  { label: 'LMS for ANSPs and CAAs', path: '/lms-ansp-caa' },
  { label: 'LMS for Airlines', path: '/lms-airlines' },
];

const LmsAviationCompaniesPage: React.FC = () => {
  return (
    <LmsRolePage
      title="AviaGov LMS for Aviation Companies"
      seoTitle="AviaGov LMS for Aviation Companies | Training, Certification and Compliance Records"
      description="Aviation LMS for operators, MROs, ATOs, service providers, and regulated aviation businesses that need controlled training delivery, certification, qualification evidence, and partner access."
      eyebrow="Aviation Companies"
      heroNote="Built for regulated aviation businesses that need defensible training records without airline or authority-specific overhead."
      heroImagePrompt="A sophisticated aviation company training operations scene with controlled course delivery, certificates, partner portal access, compliance dashboards, and qualification evidence, dark enterprise visual language"
      heroImageAlt="Aviation company LMS view showing controlled course delivery, certification, partner access, and compliance dashboards"
      painPoints={[
        'Built for regulated aviation businesses such as operators, maintenance organizations, training providers, airport service companies, and contractor-heavy environments.',
        'Many regulated aviation companies need defensible training records but do not need a generic consumer LMS or a full airline training stack.',
        'Training delivery for employees, contractors, and partners often lacks consistent governance, certificate control, and audit traceability.',
        'Operational findings and compliance actions are difficult to connect to concrete training remediation.',
        'External access is sometimes needed, but organizations still need to control domain, visibility, and certificate scope.',
      ]}
      capabilities={[
        'Internal LMS delivery with courses, lessons, assessments, cohorts, certificates, and learner analytics.',
        'Internal LMS delivery with gradebook, analytics, cohort segmentation, live session orchestration, SCORM/xAPI, file submissions, slide activities, and learner progression controls.',
        'Governed qualification records, recurrence policies, export profiles, and evidence retention where aviation compliance training needs more than course completion.',
        'Controlled public portal access under the company domain with branded delivery, learner self-service, certificates, announcements, discussions, and optional billing or subscription flows.',
        'SMS-linked training workflows so safety findings can trigger remedial training and evidence-backed closure.',
        'Role-based access, modular activation, and deployment choices aligned with enterprise governance requirements.',
      ]}
      workflow={[
        {
          title: 'Deliver structured training',
          description: 'Create courses, cohorts, live sessions, and assessments for employees or external learners while keeping the delivery layer under company governance and ready for commercial or regulated training use cases.',
          items: [
            'Catalog, enrollments, learner dashboard, and portal self-service',
            'SCORM/xAPI, documents, slides, quizzes, live sessions, and submissions',
            'Certificates, announcements, discussions, and verification tracking',
          ],
        },
        {
          title: 'Add governance only where needed',
          description: 'For regulated roles, extend delivery with qualification programs, recurrence policies, qualification records, and export profiles so the company can scale from standard LMS operations into governed regulated training without changing systems.',
          items: [
            'Qualification records by role or regulated function',
            'Recurrence, validity handling, and compliance-pack overlays',
            'Audit-oriented evidence retention and export structures',
          ],
        },
        {
          title: 'Open controlled external access',
          description: 'Enable a branded portal for contractors, suppliers, or partner organizations without turning the platform into an open marketplace or losing visibility over training outcomes, commerce settings, or governed records.',
          items: [
            'Customer-domain portal, login controls, and external learner onboarding',
            'Partner and contractor certificate access with learner-facing progress',
            'Organization-controlled visibility, billing, subscription, and access rules',
          ],
        },
      ]}
      outcomes={[
        'A cleaner training operating model for regulated aviation companies that need governance without unnecessary airline or authority complexity.',
        'Stronger audit posture through traceable training history, certificate status, and qualification evidence retention.',
        'A clear bridge from branded training portal delivery to governed regulated training without replacing the LMS stack as requirements mature.',
      ]}
      keywords={[
        'aviation company LMS',
        'aviation training LMS',
        'regulated aviation training platform',
        'aviation compliance training',
        'aviation training records',
        'recurrent training aviation',
        'contractor training portal',
        'aviation certificate tracking',
      ]}
      faq={[
        {
          question: 'Is AviaGov LMS only for airlines and authorities?',
          answer: 'No. It also fits regulated aviation companies that need controlled training delivery, certificates, qualification evidence, and optional external learner access without airline-specific operational layers.',
        },
        {
          question: 'Can aviation companies train contractors or partners through the LMS?',
          answer: 'Yes. AviaGov supports a controlled portal under the organization domain so companies can train external learners while retaining access rules, visibility, and certificate control.',
        },
        {
          question: 'Can training records connect to compliance or safety actions?',
          answer: 'Yes. AviaGov can connect LMS delivery with governed evidence and SMS-linked training actions so remediation and completion remain traceable.',
        },
      ]}
      relatedLinks={relatedLinks}
      ctaId="lms_aviation_companies_walkthrough"
    />
  );
};

export default LmsAviationCompaniesPage;
